import { useCallback, useState } from "react";

import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { DisplayError } from "@/utils";

import { useWriteOffFinanceEntryMutation } from "./use-write-off-finance-entry-mutation";

/** How long "Desfazer" stays on screen before the write-off is committed. */
export const WRITE_OFF_UNDO_WINDOW_MS = 10_000;

export type DeferredWriteOffEntry = {
  id: string;
  name: string;
  total: string;
};

type Scheduled = {
  timeout: ReturnType<typeof setTimeout>;
  dismiss: () => void;
};

/**
 * `POST /finance-entries/{id}/pay` has no counterpart on the API: once an entry
 * is paid nothing puts it back. So the undo is a deferred commit — the request
 * leaves only when the window closes without an undo, and the row is shown as
 * paid in the meantime.
 *
 * The window lives in this module, not in component state, so leaving the page
 * inside the window still commits what the user asked for; only "Desfazer"
 * cancels it.
 */
const scheduled = new Map<string, Scheduled>();

export function useDeferredWriteOff() {
  const { mutateAsync } = useWriteOffFinanceEntryMutation();
  const { toast } = useToast();
  const [paidLocally, setPaidLocally] = useState<readonly string[]>([]);

  const forget = useCallback((id: string) => {
    setPaidLocally((ids) => ids.filter((current) => current !== id));
  }, []);

  const undoWriteOff = useCallback(
    (id: string) => {
      const pending = scheduled.get(id);
      if (!pending) return;

      clearTimeout(pending.timeout);
      scheduled.delete(id);
      pending.dismiss();
      forget(id);
      toast({ title: "Baixa desfeita" });
    },
    [forget, toast]
  );

  const commit = useCallback(
    async (entry: DeferredWriteOffEntry) => {
      scheduled.delete(entry.id);
      try {
        await mutateAsync({ id: entry.id });
      } catch (error) {
        forget(entry.id);
        toast({
          title: "Não foi possível dar baixa",
          description: DisplayError(error),
          variant: "destructive",
        });
      }
    },
    [forget, mutateAsync, toast]
  );

  const scheduleWriteOff = useCallback(
    (entry: DeferredWriteOffEntry) => {
      if (scheduled.has(entry.id)) return;

      setPaidLocally((ids) => (ids.includes(entry.id) ? ids : [...ids, entry.id]));

      const handle = toast({
        title: "Baixa registrada",
        description: `${entry.name} · ${entry.total}`,
        duration: WRITE_OFF_UNDO_WINDOW_MS,
        action: (
          <ToastAction altText="Desfazer a baixa" onClick={() => undoWriteOff(entry.id)}>
            Desfazer
          </ToastAction>
        ),
      });

      scheduled.set(entry.id, {
        dismiss: handle.dismiss,
        timeout: setTimeout(() => {
          handle.dismiss();
          void commit(entry);
        }, WRITE_OFF_UNDO_WINDOW_MS),
      });
    },
    [commit, toast, undoWriteOff]
  );

  const isPaidLocally = useCallback(
    (id: string) => paidLocally.includes(id),
    [paidLocally]
  );

  return { scheduleWriteOff, undoWriteOff, isPaidLocally };
}
